const siteKey = import.meta.env.VUE_APP_RECAPTCHA_KEY;

let loadPromise = null;

// Google serves the identical v3 API from two hosts. `recaptcha.net` is the one
// reachable from regions where google.com is blocked — Bugsnag showed the load
// failing for every Chinese client (VEX-453) — and it is the default.
//
// It only completes its handshake on a secure origin though: measured on the
// same browser, `grecaptcha.execute` resolves in ~0.4s from recaptcha.net on
// https://business.pepper.com but never resolves on http://localhost:8082
// (it sits until reCAPTCHA's own 30s timeout), where google.com answers in
// ~2s. So http development keeps google.com.
const RECAPTCHA_NET_HOST = 'https://www.recaptcha.net';
const GOOGLE_HOST = 'https://www.google.com';

/**
 * @description Pick the reCAPTCHA host to load the v3 script from
 * @param protocol
 * @returns {string}
 */
export function recaptchaScriptHost(protocol = window.location.protocol) {
  return protocol === 'https:' ? RECAPTCHA_NET_HOST : GOOGLE_HOST;
}

/**
 * Inject the reCAPTCHA v3 script on demand (idempotent).
 *
 * Once the script runs, Google keeps its badge in the DOM for the whole
 * session, so we only load it on the guest/auth pages that actually call
 * grecaptcha.execute (login / register / forgot / reset) rather than globally
 * at app startup.
 */
export function loadRecaptcha() {
  if (!siteKey) {
    return Promise.resolve(false);
  }

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${recaptchaScriptHost()}/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => {
        // Let a later attempt retry instead of caching the failure forever.
        loadPromise = null;
        reject(new Error('Failed to load reCAPTCHA'));
      };
      document.head.appendChild(script);
    });
  }

  return loadPromise;
}

/**
 * Run a reCAPTCHA v3 action and resolve with its token.
 *
 * Loads the script first when it is not on the page yet, so submitting a form
 * before the lazy load finished cannot leave `window.grecaptcha` undefined.
 *
 * Rejects when the script cannot be loaded or when Google's own challenge
 * times out, so callers must handle that — the `recaptcha` mixin does it for
 * every form.
 */
export function executeRecaptcha(action) {
  // Already on the page (or stubbed in tests): skip the loader entirely.
  const ready = window.grecaptcha ? Promise.resolve(true) : loadRecaptcha();

  return ready.then((isLoaded) => {
    if (!isLoaded) {
      return '';
    }

    return new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
      });
    });
  });
}
